/**
 * Governance Hub: the deliverable that closes an obligation.
 *
 * One section in the detail drawer. The owner attaches the email, PDF, Word
 * document or other file that proves the item was done, and can mark the
 * item complete in the same step. Files are listed with who attached them
 * and when, each one downloadable, each one removable by the person who
 * attached it or by compliance. Nothing here touches the rest of the record.
 */
import React, { useEffect, useRef, useState } from 'react';

const API = process.env.REACT_APP_API_URL || '';
const ACCEPT = '.eml,.msg,.pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.txt,.png,.jpg,.jpeg';

const fmtSize = (n) => {
  const b = Number(n) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const fmtWhen = (iso) => {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const kindOf = (name) => {
  const ext = String(name || '').toLowerCase().split('.').pop();
  if (ext === 'eml' || ext === 'msg') return { label: 'Email', cls: 'bg-sky-100 text-sky-700 border-sky-200' };
  if (ext === 'pdf') return { label: 'PDF', cls: 'bg-rose-100 text-rose-700 border-rose-200' };
  if (ext === 'doc' || ext === 'docx') return { label: 'Word', cls: 'bg-blue-100 text-blue-700 border-blue-200' };
  if (ext === 'xls' || ext === 'xlsx' || ext === 'csv') return { label: 'Excel', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' };
  if (ext === 'ppt' || ext === 'pptx') return { label: 'Slides', cls: 'bg-orange-100 text-orange-700 border-orange-200' };
  if (ext === 'png' || ext === 'jpg' || ext === 'jpeg') return { label: 'Image', cls: 'bg-violet-100 text-violet-700 border-violet-200' };
  return { label: 'File', cls: 'bg-slate-100 text-slate-600 border-slate-200' };
};

export default function DeliverablesSection({ item, isCompleted, onObligationUpdated }) {
  const [files, setFiles] = useState(null); // null until loaded
  const [picked, setPicked] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(''); // '' | 'attach' | 'complete' | 'remove:<id>'
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    let alive = true;
    setFiles(null); setPicked(null); setNote(''); setMessage(''); setError('');
    fetch(`${API}/api/calendar/obligations/${item.id}/deliverables`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { deliverables: [] }))
      .then((d) => { if (alive) setFiles(Array.isArray(d.deliverables) ? d.deliverables : []); })
      .catch(() => { if (alive) setFiles([]); });
    return () => { alive = false; };
  }, [item.id]);

  const choose = (file) => {
    setError(''); setMessage('');
    if (!file) { setPicked(null); return; }
    const ext = String(file.name || '').toLowerCase().split('.').pop();
    if (!ACCEPT.split(',').includes(`.${ext}`)) {
      setPicked(null);
      setError('Attach an email (.eml, .msg), PDF, Word, Excel, PowerPoint, CSV, text or image file.');
      return;
    }
    if (file.size > 25 * 1024 * 1024) { setPicked(null); setError('The file is larger than 25 MB.'); return; }
    setPicked(file);
  };

  const upload = async (markComplete) => {
    if (!picked || busy) return;
    setBusy(markComplete ? 'complete' : 'attach'); setError(''); setMessage('');
    try {
      const fd = new FormData();
      fd.append('file', picked);
      if (note.trim()) fd.append('note', note.trim());
      if (markComplete) fd.append('mark_complete', 'true');
      const res = await fetch(`${API}/api/calendar/obligations/${item.id}/deliverables`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'The deliverable could not be attached.');
      setFiles(Array.isArray(data.deliverables) ? data.deliverables : []);
      setPicked(null); setNote('');
      if (inputRef.current) inputRef.current.value = '';
      setMessage(markComplete ? 'Attached. The item is marked complete.' : 'Attached.');
      onObligationUpdated?.(item.id, {
        deliverable_count: (data.deliverables || []).length,
        ...(markComplete && data.display_status ? { display_status: data.display_status } : {}),
      });
    } catch (e) {
      setError(e.message || 'The deliverable could not be attached.');
    } finally {
      setBusy('');
    }
  };

  const remove = async (f) => {
    if (busy) return;
    setBusy(`remove:${f.id}`); setError(''); setMessage('');
    try {
      const res = await fetch(`${API}/api/calendar/obligations/${item.id}/deliverables/${f.id}`, {
        method: 'DELETE', credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'The file could not be removed.');
      setFiles(Array.isArray(data.deliverables) ? data.deliverables : []);
      onObligationUpdated?.(item.id, { deliverable_count: (data.deliverables || []).length });
    } catch (e) {
      setError(e.message || 'The file could not be removed.');
    } finally {
      setBusy('');
    }
  };

  const count = files ? files.length : Number(item.deliverable_count) || 0;

  return (
    <div className="rounded-xl bg-white border border-slate-200 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Deliverable</h4>
        <span className="text-xs text-slate-400">{count === 0 ? 'Nothing attached' : `${count} file${count === 1 ? '' : 's'}`}</span>
      </div>
      <p className="text-xs text-slate-500">
        Attach the email, PDF, Word document or other file that closes this item.
      </p>

      {files === null ? (
        <p className="text-xs text-slate-400">Loading…</p>
      ) : files.length > 0 && (
        <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
          {files.map((f) => {
            const k = kindOf(f.file_name);
            return (
              <li key={f.id} className="flex items-start gap-3 px-3 py-2 bg-white">
                <span className={`mt-0.5 inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase border ${k.cls}`}>{k.label}</span>
                <div className="flex-1 min-w-0">
                  <a
                    href={`${API}/api/calendar/obligations/${item.id}/deliverables/${f.id}/download`}
                    className="block text-sm font-medium text-slate-800 hover:text-orange-700 truncate"
                    title={f.file_name}
                  >
                    {f.file_name}
                  </a>
                  <div className="text-xs text-slate-500 truncate">
                    {[fmtSize(f.size_bytes), f.uploaded_by_name || f.uploaded_by_email, fmtWhen(f.created_at)].filter(Boolean).join('  ·  ')}
                  </div>
                  {f.note && <div className="text-xs text-slate-600 mt-0.5 whitespace-pre-wrap">{f.note}</div>}
                </div>
                {f.can_remove && (
                  <button
                    type="button"
                    onClick={() => remove(f)}
                    disabled={!!busy}
                    className="text-xs text-slate-400 hover:text-red-600 disabled:opacity-50"
                    title="Remove this file"
                  >
                    {busy === `remove:${f.id}` ? '…' : 'Remove'}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); choose(e.dataTransfer.files?.[0] || null); }}
        className={`rounded-lg border-2 border-dashed px-3 py-3 text-center transition-colors ${dragOver ? 'border-orange-400 bg-orange-50/60' : 'border-slate-200 bg-slate-50/60'}`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          onChange={(e) => choose(e.target.files?.[0] || null)}
          className="hidden"
          id={`deliverable-file-${item.id}`}
        />
        {picked ? (
          <div className="flex items-center justify-center gap-2 text-sm text-slate-700">
            <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase border ${kindOf(picked.name).cls}`}>{kindOf(picked.name).label}</span>
            <span className="truncate max-w-[14rem]" title={picked.name}>{picked.name}</span>
            <span className="text-xs text-slate-400">{fmtSize(picked.size)}</span>
            <button type="button" onClick={() => { choose(null); if (inputRef.current) inputRef.current.value = ''; }} className="text-xs text-slate-400 hover:text-slate-700">Change</button>
          </div>
        ) : (
          <label htmlFor={`deliverable-file-${item.id}`} className="cursor-pointer text-sm text-slate-600">
            <span className="font-medium text-orange-700">Choose a file</span> or drop it here
            <span className="block text-[11px] text-slate-400 mt-0.5">Email, PDF, Word, Excel, PowerPoint, CSV, text or image, up to 25 MB</span>
          </label>
        )}
      </div>

      {picked && (
        <>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={1000}
            placeholder="Note, optional"
            className="w-full text-sm bg-white border border-slate-200 rounded-lg px-3 py-1.5"
          />
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => upload(false)}
              disabled={!!busy}
              className="px-3 py-2 rounded-lg bg-white border border-slate-300 text-slate-700 hover:border-slate-400 text-sm font-medium disabled:opacity-50"
            >
              {busy === 'attach' ? 'Attaching…' : 'Attach'}
            </button>
            {!isCompleted && (
              <button
                type="button"
                onClick={() => upload(true)}
                disabled={!!busy}
                className="px-3 py-2 rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white text-sm font-semibold shadow-sm disabled:opacity-50"
              >
                {busy === 'complete' ? 'Attaching…' : 'Attach and mark complete'}
              </button>
            )}
          </div>
        </>
      )}

      {message && <p className="text-sm text-emerald-700">{message}</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
}
